// ============================================================
// FONADHOO CRM v3.0 - TEAM DASHBOARD COMPONENT
// Volunteer performance metrics and activity tracking
// ============================================================

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDate, formatPercent } from './shared/utils';
import { hasPermission, TEMP_PERMISSION_OPTIONS } from './shared/constants';
import { Icon } from './shared/Icon';

const ROLE_OPTIONS = [
  { value: 'super_admin',           label: 'Super Admin',            rank: 8 },
  { value: 'campaign_manager',      label: 'Campaign Manager',       rank: 7 },
  { value: 'candidate',             label: 'Candidate',              rank: 6 },
  { value: 'atoll_coordinator',     label: 'Atoll Coordinator',      rank: 5 },
  { value: 'city_manager',          label: 'City Manager',           rank: 4 },
  { value: 'constituency_manager',  label: 'Constituency Manager',   rank: 4 },
  { value: 'island_manager',        label: 'Island Manager',         rank: 3 },
  { value: 'outreach_manager',      label: 'Outreach Manager',       rank: 2 },
  { value: 'event_manager',         label: 'Event Manager',          rank: 1 },
  { value: 'campaign_trips_manager',label: 'Campaign Trips Manager', rank: 1 },
  { value: 'trip_organizer',        label: 'Trip Organizer',         rank: 0 },
  { value: 'patch_agent',           label: 'Patch Agent',            rank: 0 },
];
const ROLE_RANK = Object.fromEntries(ROLE_OPTIONS.map(r => [r.value, r.rank]));
const ISLANDS = ['Fonadhoo', 'Maamendhoo'];

export default function TeamDashboard({ supabase, user, allVoters = [] }) {
  const canEditRoles = hasPermission(user?.role, 'canManageUsers');
  const location = useLocation();
  const navigate = useNavigate();

  // Reset detail view whenever the user navigates to this page (including re-clicking the nav item)
  useEffect(() => {
    setSelectedMember(null);
  }, [location]);

  const [teamMembers, setTeamMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('All');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editIsland, setEditIsland] = useState('');
  const [editWard, setEditWard] = useState('');
  const [editVoterSearch, setEditVoterSearch] = useState('');
  const [editHouseholdSearch, setEditHouseholdSearch] = useState('');
  const [editHouseholdOpen, setEditHouseholdOpen] = useState(false);
  const [editHouseholdIsland, setEditHouseholdIsland] = useState(''); // island used as search pool (separate from territory)
  const [editTempPerms, setEditTempPerms] = useState([]);    // temporary permission grants
  const [editRevokedPerms, setEditRevokedPerms] = useState([]); // role permissions explicitly revoked
  const [editAssignedVoters, setEditAssignedVoters] = useState([]); // {id, name, island, ward}
  const [editTerritories, setEditTerritories] = useState([]); // multi-island list for constituency_manager
  const [assignedExpanded, setAssignedExpanded] = useState(false);
  const [expandedHouseholds, setExpandedHouseholds] = useState(() => new Set());
  const toggleHousehold = (key) => setExpandedHouseholds(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const [expandedDetailHH, setExpandedDetailHH] = useState(() => new Set());
  const toggleDetailHH = (key) => setExpandedDetailHH(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const [showPerms, setShowPerms] = useState(false);
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  const toggleSection = (key) => setExpandedSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    island: '',
    role: 'patch_agent'
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, [dateRange, allVoters.length]);

  const removeUser = async (member) => {
    if (member.role === 'super_admin') return;
    if (!window.confirm(`Remove ${member.full_name}? This will permanently delete their account.`)) return;
    const { data, error } = await supabase.functions.invoke('delete-user', { body: { id: member.id } });
    if (error || data?.error) {
      // Fall back to soft-deactivate if the Edge Function isn't deployed yet
      console.error('delete-user function failed:', error || data?.error);
      await supabase.from('profiles').update({ is_active: false }).eq('id', member.id);
      alert((data?.error || error?.message || 'Server delete failed') + ' — user deactivated instead.');
    }
    if (selectedMember?.id === member.id) setSelectedMember(null);
    loadDashboard();
  };

  const openEdit = (member, e) => {
    e.stopPropagation();
    setEditingMember(member);
    setEditRole(member.role);
    const territory = member.assigned_territory || '';
    setEditIsland(ISLANDS.includes(territory) ? territory : '');
    setEditWard(!ISLANDS.includes(territory) ? territory : '');
    setEditVoterSearch('');
    setEditHouseholdSearch('');
    setEditHouseholdOpen(false);
    setEditHouseholdIsland('');
    setEditAssignedVoters([]);
    setEditTerritories(member.assigned_territories || []);
    setEditTempPerms(member.temp_permissions || []);
    setEditRevokedPerms(member.revoked_permissions || []);
    setAssignedExpanded(false);
    setExpandedHouseholds(new Set());
    setShowPerms(false);
    setError('');
  };

  // Load individually assigned voters whenever edit modal opens
  useEffect(() => {
    if (!editingMember) return;
    setLoadingAssigned(true);
    supabase
      .from('voters')
      .select('id, name, island, ward, household_id, house_name, house')
      .eq('gotv_assigned_to', editingMember.id)
      .order('name')
      .then(({ data, error }) => {
        if (error) console.error('[team] assigned voters load failed:', error);
        setEditAssignedVoters(data || []);
        setLoadingAssigned(false);
      });
  }, [editingMember?.id]);

  const addVoterAssignment = (voter) => {
    if (editAssignedVoters.find(v => v.id === voter.id)) return;
    setEditAssignedVoters(prev => [...prev, { id: voter.id, name: voter.name, island: voter.island, ward: voter.ward, household_id: voter.household_id, house_name: voter.house_name, house: voter.house }]);
    setEditVoterSearch('');
  };

  const removeVoterAssignment = (voterId) => {
    setEditAssignedVoters(prev => prev.filter(v => v.id !== voterId));
  };

  const saveRole = async () => {
    if (!editingMember) return;
    setSaving(true);
    setError('');

    const isCM = editRole === 'constituency_manager';
    const territory = isCM ? null : (editWard || editIsland || null);
    const territories = isCM ? editTerritories : [];
    // 1. Update profile (role + territory). Constituency Manager uses the
    //    multi-island list; everyone else uses the single territory.
    const { error: profileErr } = await supabase
      .from('profiles')
      .update({ role: editRole, assigned_territory: territory, assigned_territories: territories, temp_permissions: editTempPerms, revoked_permissions: editRevokedPerms })
      .eq('id', editingMember.id);
    if (profileErr) { setError(profileErr.message); setSaving(false); return; }

    // 2. Clear old individual assignments for this agent
    await supabase.from('voters').update({ gotv_assigned_to: null }).eq('gotv_assigned_to', editingMember.id);

    // 3. Set new individual assignments
    if (editAssignedVoters.length > 0) {
      await supabase
        .from('voters')
        .update({ gotv_assigned_to: editingMember.id })
        .in('id', editAssignedVoters.map(v => v.id));
    }

    setSaving(false);
    setEditingMember(null);
    loadDashboard();
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      setError('Please fill all fields');
      return;
    }

    setCreating(true);
    setError('');

    try {
      // Server-side via Edge Function — keeps the service role key off the browser
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUser.email,
          password: newUser.password,
          full_name: newUser.full_name,
          role: newUser.role,
          assigned_territory: newUser.island || null,
        },
      });
      if (error) throw new Error(error.message || 'create-user function failed');
      if (data?.error) throw new Error(data.error);

      setNewUser({ email: '', password: '', full_name: '', island: '', role: 'patch_agent' });
      setShowAddUser(false);
      loadDashboard();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };


  const loadDashboard = async () => {
    setLoading(true);
    
    // Load team members — only show roles at or below current user's rank
    const myRank = ROLE_RANK[user?.role] ?? 0;
    const { data: rawMembers } = await supabase
      .from('profiles')
      .select('*')
      .eq('is_active', true)
      .order('full_name');

    if (!rawMembers) { setLoading(false); return; }
    const members = rawMembers.filter(m => (ROLE_RANK[m.role] ?? 0) <= myRank);

    // Calculate date range
    const now = new Date();
    let startDate;

    if (dateRange === 'Week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (dateRange === 'Month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      startDate = new Date('2020-01-01'); // All time
    }

    const startDateStr = startDate.toISOString().split('T')[0];

    const NEGATIVES = ['No Answer / Not Home', 'Busy / Call Back Later', 'Refused to Talk', 'Hostile Response'];

    // Two queries in parallel — voters come from the prop (already loaded in App.jsx)
    const [{ data: allOutreach, error: outreachErr }, { data: allReminders, error: remindersErr }] = await Promise.all([
      supabase.from('outreach').select('logged_by, voter_id, outcome').gte('date', startDateStr),
      supabase.from('reminders').select('assigned_to').eq('completed', true).gte('completed_at', startDateStr),
    ]);
    if (outreachErr) console.error('[team] outreach load failed:', outreachErr);
    if (remindersErr) console.error('[team] reminders load failed:', remindersErr);
    // Build assignedRows from the prop instead of an extra query
    const assignedRows = (allVoters || []).filter(v => v.gotv_assigned_to).map(v => ({ gotv_assigned_to: v.gotv_assigned_to }));

    const assignedByMember = {};
    for (const v of assignedRows || []) {
      assignedByMember[v.gotv_assigned_to] = (assignedByMember[v.gotv_assigned_to] || 0) + 1;
    }

    // Group outreach by logged_by
    const outreachByMember = {};
    for (const row of allOutreach || []) {
      if (!outreachByMember[row.logged_by]) outreachByMember[row.logged_by] = [];
      outreachByMember[row.logged_by].push(row);
    }
    // Group reminders by assigned_to
    const remindersByMember = {};
    for (const row of allReminders || []) {
      remindersByMember[row.assigned_to] = (remindersByMember[row.assigned_to] || 0) + 1;
    }

    const enrichedMembers = members.map(member => {
      const rows = outreachByMember[member.id] || [];
      const outreachCount    = rows.length;
      const successfulCount  = rows.filter(r => !NEGATIVES.includes(r.outcome)).length;
      const commitmentsCount = rows.filter(r => r.outcome === 'Commitment Made').length;
      const uniqueVoters     = new Set(rows.map(r => r.voter_id)).size;
      const remindersCount   = remindersByMember[member.id] || 0;
      return {
        ...member,
        assigned_count:    member.assigned_territory
          ? allVoters.filter(v => v.island === member.assigned_territory || v.ward === member.assigned_territory).length
          : (Array.isArray(member.assigned_territories) && member.assigned_territories.length
              ? allVoters.filter(v => member.assigned_territories.includes(v.island) || member.assigned_territories.includes(v.ward)).length
              : (assignedByMember[member.id] || 0)),
        outreach_count:    outreachCount,
        successful_count:  successfulCount,
        commitments_count: commitmentsCount,
        reminders_count:   remindersCount,
        unique_voters:     uniqueVoters,
        success_rate:      outreachCount > 0 ? (successfulCount / outreachCount) * 100 : 0,
      };
    });

    // Calculate overall stats
    const totalAssigned    = enrichedMembers.reduce((sum, m) => sum + m.assigned_count, 0);
    const totalOutreach    = enrichedMembers.reduce((sum, m) => sum + m.outreach_count, 0);
    const totalSuccessful  = enrichedMembers.reduce((sum, m) => sum + m.successful_count, 0);
    const totalCommitments = enrichedMembers.reduce((sum, m) => sum + m.commitments_count, 0);
    const totalReminders   = enrichedMembers.reduce((sum, m) => sum + m.reminders_count, 0);

    setTeamMembers(enrichedMembers.sort((a, b) => b.outreach_count - a.outreach_count));
    setStats({
      total_assigned: totalAssigned,
      total_outreach: totalOutreach,
      total_successful: totalSuccessful,
      total_commitments: totalCommitments,
      total_reminders: totalReminders,
      avg_success_rate: totalOutreach > 0 ? (totalSuccessful / totalOutreach) * 100 : 0
    });
    
    setLoading(false);
  };

  const getMedalIcon = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `${index + 1}.`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-2)' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--border-2)',
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        Loading dashboard...
      </div>
    );
  }

  const memberAssignedVoters = selectedMember ? allVoters.filter(v => v.gotv_assigned_to === selectedMember.id) : [];
  const isMobile = window.innerWidth < 768;

  return (
    <div>
      {/* ── Member detail view ── */}
      {selectedMember && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: 8 }}>
            <button
              onClick={() => setSelectedMember(null)}
              style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-2)', background: 'var(--border-2)', color: 'var(--text)', fontSize: '14px', cursor: 'pointer' }}
            >
              ← Back to Team
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              {canEditRoles && selectedMember.role !== 'super_admin' && (
                <button
                  onClick={e => openEdit(selectedMember, e)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: '8px', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', fontSize: '14px', cursor: 'pointer' }}
                >
                  <Icon name="edit" size={14} /> Edit Assignments
                </button>
              )}
              {selectedMember.role !== 'super_admin' && (
                <button
                  onClick={() => removeUser(selectedMember)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: '8px', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: '14px', cursor: 'pointer' }}
                >
                  <Icon name="trash" size={14} /> Remove User
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '24px', marginBottom: '16px' }}>
            <div style={{ marginBottom: '18px' }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px' }}>{selectedMember.full_name}</h2>
              <div style={{ fontSize: '12px', color: 'var(--text-3)', fontFamily: "'DM Mono', monospace", marginTop: 4 }}>
                {selectedMember.role}
                {selectedMember.assigned_territory && <span style={{ marginLeft: 8, color: '#3b82f6' }}>· {selectedMember.assigned_territory}</span>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }}>
              {(() => {
                const isLeaderRole = ['super_admin', 'campaign_manager', 'candidate'].includes(selectedMember.role);
                return [
                  !isLeaderRole && { value: selectedMember.assigned_count, label: 'Total Assigned', color: '#8b5cf6', nav: null,         icon: 'user' },
                  { value: selectedMember.outreach_count,                    label: 'Reach Attempts', color: '#3b82f6', nav: 'attempts',   icon: 'phone' },
                  { value: selectedMember.successful_count,                  label: 'Successful',     color: '#10b981', nav: 'successful', icon: 'check' },
                  { value: selectedMember.commitments_count,                 label: 'Commitments',    color: '#f59e0b', nav: 'commitments', icon: 'target' },
                  { value: `${Math.round(selectedMember.success_rate)}%`,    label: 'Success Rate',   color: '#06b6d4', nav: null,         icon: 'trending' },
                ].filter(Boolean).map(({ value, label, color, nav, icon }) => (
                  <div key={label}
                    onClick={nav ? () => navigate('/outreach', { state: { memberId: selectedMember.id, statFilter: nav === 'attempts' ? null : nav } }) : undefined}
                    style={{ background: 'var(--surface-2)', padding: '14px 16px', borderRadius: '12px', minHeight: 96, border: '1px solid var(--border)', position: 'relative', cursor: nav ? 'pointer' : 'default', transition: 'border-color 0.15s, transform 0.15s' }}
                    title={nav ? 'View in Outreach' : undefined}
                    onMouseEnter={e => { if (nav) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; } }}
                    onMouseLeave={e => { if (nav) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                      <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                        <Icon name={icon} size={15} />
                      </span>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Assigned voters — hidden for leader roles */}
          {!['super_admin', 'campaign_manager', 'candidate'].includes(selectedMember.role) && (() => {
            const territory = selectedMember.assigned_territory;
            const terrs = Array.isArray(selectedMember.assigned_territories) ? selectedMember.assigned_territories : [];
            const isTerritoryBased = !!territory || terrs.length > 0;
            const effectiveVoters = territory
              ? allVoters.filter(v => v.island === territory || v.ward === territory)
              : terrs.length > 0
                ? allVoters.filter(v => terrs.includes(v.island) || terrs.includes(v.ward))
                : memberAssignedVoters;

            // Group by household
            const hhMap = {};
            for (const v of effectiveVoters) {
              const key = v.household_id || v.house_name || v.house || '__none__';
              if (!hhMap[key]) hhMap[key] = { key, label: v.house_name || v.house || (key === '__none__' ? 'No Household' : key), ward: v.ward, assigned: [] };
              hhMap[key].assigned.push(v);
            }
            const households = Object.values(hhMap).map(hh => {
              const total = (isTerritoryBased || hh.key === '__none__') ? hh.assigned.length
                : allVoters.filter(v => (v.household_id || v.house_name || v.house || '__none__') === hh.key).length;
              return { ...hh, total };
            }).sort((a, b) => a.label.localeCompare(b.label));

            return (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    Assigned Voters
                    <span style={{ fontSize: '13px', color: 'var(--text-3)', fontWeight: 400 }}>({effectiveVoters.length})</span>
                    {isTerritoryBased && (
                      <span style={{ fontSize: 10, background: '#0c2a1a', color: '#34d399', border: '1px solid #065f46', borderRadius: 4, padding: '2px 7px', fontFamily: "'DM Mono', monospace" }}>
                        📍 Full Territory · {territory}
                      </span>
                    )}
                  </h3>
                  {canEditRoles && !isTerritoryBased && (
                    <button
                      onClick={e => openEdit(selectedMember, e)}
                      style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}
                    >
                      + Assign
                    </button>
                  )}
                </div>

                {effectiveVoters.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-4)', fontSize: '13px', fontFamily: "'DM Mono', monospace" }}>
                    No voters assigned yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {households.map(hh => {
                      const isOpen = expandedDetailHH.has(hh.key);
                      const missing = hh.total - hh.assigned.length;
                      const incomplete = !isTerritoryBased && missing > 0;
                      return (
                        <div key={hh.key} style={{ background: 'var(--surface-2)', borderRadius: 8, overflow: 'hidden', border: `1px solid ${incomplete ? '#78350f' : 'var(--border)'}` }}>
                          <div onClick={() => toggleDetailHH(hh.key)} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', gap: 8 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{isOpen ? '▼' : '▶'}</span>
                            <span style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)', fontWeight: 500 }}>{hh.label}</span>
                            {hh.ward && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{hh.ward}</span>}
                            {!isTerritoryBased && (
                              <span style={{ fontSize: 11, color: incomplete ? '#f59e0b' : 'var(--text-4)' }}>{hh.assigned.length}/{hh.total}</span>
                            )}
                            {incomplete && (
                              <span style={{ fontSize: 10, background: '#78350f', color: '#fbbf24', borderRadius: 4, padding: '1px 6px', fontFamily: "'DM Mono', monospace" }}>
                                {missing} missing
                              </span>
                            )}
                          </div>
                          {isOpen && (
                            <div style={{ borderTop: '1px solid #1e2d40', padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {hh.assigned.map(v => (
                                <div key={v.id} style={{ fontSize: 12, color: 'var(--text-2)', padding: '3px 0' }}>{v.name}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Main list (hidden while detail view is open) ── */}
      {!selectedMember && <div>

      {/* Header */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        borderRadius: '14px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6', flexShrink: 0 }}>
              <Icon name="users" size={20} />
            </span>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Performance</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Team Dashboard</h2>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => setShowAddUser(true)}
              style={{
                padding: '8px 14px',
                borderRadius: '9px',
                border: 'none',
                background: '#15803d',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontFamily: "'DM Mono', monospace",
                transition: 'filter 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.15)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
              <Icon name="plus" size={15} color="#fff" /> Add User
            </button>

            {['Week', 'Month', 'All'].map(range => {
              const on = dateRange === range;
              return (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '9px',
                    border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                    background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                    color: on ? '#60a5fa' : 'var(--text-2)',
                    fontSize: '12.5px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'DM Mono', monospace",
                    transition: 'background 0.15s, border-color 0.15s, color 0.15s'
                  }}
                  onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                  onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
                >
                  {range === 'Week' ? 'This Week' : range === 'Month' ? 'This Month' : 'All Time'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Overall Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px'
        }}>
          {[
            { value: allVoters.length,                         label: 'Total Voters',   color: '#64748b', nav: null,          icon: 'users' },
            { value: stats.total_assigned,                     label: 'Total Assigned', color: '#8b5cf6', nav: null,          icon: 'user' },
            { value: stats.total_outreach,                     label: 'Reach Attempts', color: '#3b82f6', nav: 'attempts',    icon: 'phone' },
            { value: stats.total_successful,                   label: 'Successful',     color: '#10b981', nav: 'successful',  icon: 'check' },
            { value: stats.total_commitments,                  label: 'Commitments',    color: '#f59e0b', nav: 'commitments', icon: 'target' },
            { value: `${Math.round(stats.avg_success_rate)}%`, label: 'Avg Success',    color: '#06b6d4', nav: null,          icon: 'trending' },
          ].map(({ value, label, color, nav, icon }) => (
            <div key={label}
              onClick={nav ? () => navigate('/outreach', { state: { statFilter: nav === 'attempts' ? null : nav } }) : undefined}
              style={{
                background: 'var(--surface-2)', padding: '14px 16px', borderRadius: '12px',
                minHeight: 96, position: 'relative',
                border: '1px solid var(--border)',
                cursor: nav ? 'pointer' : 'default',
                transition: 'border-color 0.15s, transform 0.15s',
              }}
              onMouseEnter={e => { if (nav) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; } }}
              onMouseLeave={e => { if (nav) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; } }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                  <Icon name={icon} size={15} />
                </span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Leaderboard */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        borderRadius: '14px',
        padding: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#f59e0b1f', color: '#f59e0b', flexShrink: 0 }}><Icon name="trophy" size={16} /></span>
          Team Leaderboard
        </h3>

        {(() => {
          // Only these roles belong in "Leaders" section
          const LEADER_ROLES = ['super_admin', 'campaign_manager', 'candidate'];

          // Build ward → island lookup once from the voter set. Used to roll a
          // ward-scoped territory up to its parent island for the leaderboard
          // grouping so a user assigned to "Ward 5" still shows under
          // "Fonadhoo" (not as a top-level island row of its own).
          const wardToIsland = {};
          const knownIslands = new Set();
          for (const v of allVoters) {
            if (v.island) knownIslands.add(v.island);
            if (v.ward && v.island && !(v.ward in wardToIsland)) {
              wardToIsland[v.ward] = v.island;
            }
          }

          // Determine effective island for a member:
          //  - True leaders → __leaders__
          //  - Has assigned_territory matching a known island → that island
          //  - Has assigned_territory matching a known ward → its parent island
          //  - Patch agent with voters → infer island from their voters
          //  - Otherwise → __unassigned__ (no island, not a leader)
          const getEffectiveTerritory = (m) => {
            if (LEADER_ROLES.includes(m.role)) return '__leaders__';
            if (m.assigned_territory) {
              if (knownIslands.has(m.assigned_territory)) return m.assigned_territory;
              if (wardToIsland[m.assigned_territory]) return wardToIsland[m.assigned_territory];
              return m.assigned_territory; // unknown territory string — fall back
            }
            const assigned = allVoters.filter(v => String(v.gotv_assigned_to) === String(m.id));
            if (!assigned.length) return '__unassigned__';
            const counts = {};
            for (const v of assigned) { if (v.island) counts[v.island] = (counts[v.island] || 0) + 1; }
            return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || '__unassigned__';
          };

          // Group members by effective territory
          const grouped = {};
          for (const m of teamMembers) {
            const key = getEffectiveTerritory(m);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(m);
          }
          // Sort within each group by role rank desc, then outreach desc
          Object.values(grouped).forEach(g =>
            g.sort((a, b) => (ROLE_RANK[b.role] ?? 0) - (ROLE_RANK[a.role] ?? 0) || b.outreach_count - a.outreach_count)
          );
          // Section order: Leaders → Islands (alphabetical) → Unassigned
          const islands = Object.keys(grouped).filter(k => k !== '__leaders__' && k !== '__unassigned__').sort();
          const sections = [
            ...(grouped['__leaders__'] ? ['__leaders__'] : []),
            ...islands,
            ...(grouped['__unassigned__'] ? ['__unassigned__'] : []),
          ];

          const MemberRow = ({ member, showStats = true }) => (
            <div
              key={member.id}
              onClick={e => { if (!e.target.closest('[data-nav-stat]')) setSelectedMember(member); }}
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px 16px', cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                {/* Name & Role */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {member.full_name}
                    {member.temp_permissions?.length > 0 && (
                      <span title="Has temporary permissions" style={{ fontSize: 10, background: '#451a03', color: '#f59e0b', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>⚡ temp</span>
                    )}
                  </h4>
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', textTransform: 'capitalize' }}>
                    {member.role.replace(/_/g, ' ')}
                  </div>
                </div>

                {/* Stats */}
                {showStats && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: isMobile ? '10px' : '18px', textAlign: 'center', width: isMobile ? '100%' : 'auto' }}>
                    {[
                      { value: member.assigned_count,                 label: 'Assigned', color: '#8b5cf6', nav: null },
                      { value: member.outreach_count,                 label: 'Attempts', color: '#3b82f6', nav: 'attempts' },
                      { value: member.successful_count,               label: 'Success',  color: '#10b981', nav: 'successful' },
                      { value: member.commitments_count,              label: 'Commits',  color: '#f59e0b', nav: 'commitments' },
                      { value: `${Math.round(member.success_rate)}%`, label: 'Rate',     color: '#06b6d4', nav: null },
                    ].map(({ value, label, color, nav }) => (
                      <div key={label}
                        {...(nav ? { 'data-nav-stat': '1', onClick: () => navigate('/outreach', { state: { memberId: member.id, statFilter: nav === 'attempts' ? null : nav } }) } : {})}
                        style={{ cursor: nav ? 'pointer' : 'default' }}
                        title={nav ? 'View in Outreach' : undefined}
                      >
                        <div style={{ fontSize: isMobile ? '15px' : '18px', fontWeight: '700', color }}>{value}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-3)' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Edit / Remove */}
                {canEditRoles && member.role !== 'super_admin' && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button onClick={e => openEdit(member, e)} title="Edit role"
                      style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: '5px 6px', lineHeight: 0, display: 'inline-flex' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}><Icon name="edit" size={15} /></button>
                    <button onClick={e => { e.stopPropagation(); removeUser(member); }} title="Remove user"
                      style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: '5px 6px', lineHeight: 0, display: 'inline-flex' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}><Icon name="trash" size={15} /></button>
                  </div>
                )}
              </div>
            </div>
          );

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sections.map(key => {
                const isLeaders = key === '__leaders__';
                const isUnassigned = key === '__unassigned__';
                const isCollapsed = !isLeaders && !expandedSections.has(key);
                const headerColor = isLeaders ? '#b45309' : (isUnassigned ? '#ef4444' : 'var(--link)');
                const headerLabel = isLeaders ? '👑 Leaders' : (isUnassigned ? '⚠️ Unassigned' : `📍 ${key}`);
                return (
                  <div key={key}>
                    {/* Section header */}
                    <div
                      onClick={() => !isLeaders && toggleSection(key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCollapsed ? 0 : 10, cursor: isLeaders ? 'default' : 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 700, color: headerColor, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>
                        {headerLabel}
                      </span>
                      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                      <span style={{ fontSize: 11, color: 'var(--border-2)' }}>{grouped[key].length} member{grouped[key].length !== 1 ? 's' : ''}</span>
                      {!isLeaders && (
                        <span style={{ fontSize: 11, color: 'var(--border-2)', marginLeft: 4 }}>{isCollapsed ? '▶' : '▼'}</span>
                      )}
                    </div>
                    {/* Member rows */}
                    {!isCollapsed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {grouped[key].map(member => <MemberRow key={member.id} member={member} showStats={!isLeaders} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {teamMembers.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-3)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <p>No team members found</p>
          </div>
        )}
      </div>

      </div>} {/* end !selectedMember */}

    {/* Add User Modal */}
    {showAddUser && (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.9)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16
      }}>
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 500,
          padding: 24
        }}>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 20,
            color: 'var(--text-strong)',
            marginBottom: 20
          }}>
            Add New User
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--text-2)',
              marginBottom: 6,
              fontFamily: "'DM Mono', monospace"
            }}>
              Full Name
            </label>
            <input
              value={newUser.full_name}
              onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
              placeholder="Enter full name"
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-strong)',
                fontSize: 14,
                fontFamily: "'DM Mono', monospace"
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--text-2)',
              marginBottom: 6,
              fontFamily: "'DM Mono', monospace"
            }}>
              Email
            </label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              placeholder="user@email.com"
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-strong)',
                fontSize: 14,
                fontFamily: "'DM Mono', monospace"
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--text-2)',
              marginBottom: 6,
              fontFamily: "'DM Mono', monospace"
            }}>
              Password
            </label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              placeholder="Min 8 characters"
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-strong)',
                fontSize: 14,
                fontFamily: "'DM Mono', monospace"
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--text-2)',
              marginBottom: 6,
              fontFamily: "'DM Mono', monospace"
            }}>
              Island
            </label>
            <select
              value={newUser.island}
              onChange={(e) => setNewUser({...newUser, island: e.target.value})}
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: newUser.island ? 'var(--text-strong)' : 'var(--text-3)',
                fontSize: 14,
                fontFamily: "'DM Mono', monospace"
              }}
            >
              <option value="">Select island (optional)</option>
              {ISLANDS.map(i => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--text-2)',
              marginBottom: 6,
              fontFamily: "'DM Mono', monospace"
            }}>
              Role
            </label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-strong)',
                fontSize: 14,
                fontFamily: "'DM Mono', monospace"
              }}
            >
              {ROLE_OPTIONS.filter(r => r.rank <= (ROLE_RANK[user?.role] ?? 0)).map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.4)',
              color: '#ef4444',
              padding: 10,
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 16,
              fontFamily: "'DM Mono', monospace"
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowAddUser(false);
                setError('');
                setNewUser({ email: '', password: '', full_name: '', island: '', role: 'patch_agent' });
              }}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-2)',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: "'DM Mono', monospace"
              }}
            >
              Cancel
            </button>

            <button
              onClick={createUser}
              disabled={creating}
              style={{
                padding: '10px 20px',
                background: creating ? 'var(--surface-3)' : '#15803d',
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                cursor: creating ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontFamily: "'DM Mono', monospace"
              }}
            >
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* Edit User Modal */}
    {editingMember && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
        zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}>
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12, width: '100%', maxWidth: 480,
          padding: 24, maxHeight: '90vh', overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: 'var(--text-strong)' }}>
              Edit User
            </div>
            <button
              onClick={() => { setEditingMember(null); setError(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 0 0 8px', flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text-strong)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}
            >×</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 20, fontFamily: "'DM Mono', monospace" }}>
            {editingMember.full_name} · {editingMember.email}
          </div>

          {/* Role — only show roles at or below current user's rank */}
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 5, fontFamily: "'DM Mono', monospace" }}>ROLE</label>
          <select value={editRole} onChange={e => setEditRole(e.target.value)}
            style={{ width: '100%', padding: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-strong)', fontSize: 13, fontFamily: "'DM Mono', monospace", marginBottom: 18 }}>
            {ROLE_OPTIONS.filter(r => r.rank <= (ROLE_RANK[user?.role] ?? 0)).map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          {/* Territory assignment. Constituency Manager → multiple islands;
              everyone else → single island then ward (more specific wins). */}
          {editRole === 'constituency_manager' ? (
            <>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 5, fontFamily: "'DM Mono', monospace" }}>ASSIGN ISLANDS (constituency — pick one or more)</label>
              {(() => {
                const islandList = [...new Set(['Fonadhoo', 'Maamendhoo', ...allVoters.map(v => v.island).filter(Boolean)])].sort();
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
                    {islandList.map(isl => {
                      const on = editTerritories.includes(isl);
                      return (
                        <button key={isl} type="button"
                          onClick={() => setEditTerritories(prev => on ? prev.filter(x => x !== isl) : [...prev, isl])}
                          style={{
                            padding: '7px 13px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            fontFamily: "'DM Mono', monospace",
                            border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                            background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                            color: on ? '#60a5fa' : 'var(--text-2)',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                          }}>
                          <Icon name={on ? 'check' : 'mapPin'} size={13} />{isl}
                        </button>
                      );
                    })}
                    {editTerritories.length === 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-4)', alignSelf: 'center' }}>none selected — they'll see no voters</span>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 5, fontFamily: "'DM Mono', monospace" }}>ASSIGN ISLAND (bulk territory)</label>
              <select value={editIsland} onChange={e => { setEditIsland(e.target.value); setEditWard(''); if (e.target.value) setEditHouseholdIsland(e.target.value); }}
                style={{ width: '100%', padding: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-strong)', fontSize: 13, fontFamily: "'DM Mono', monospace", marginBottom: 12 }}>
                <option value=''>— None —</option>
                <option value='Fonadhoo'>Fonadhoo</option>
                <option value='Maamendhoo'>Maamendhoo</option>
              </select>

              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 5, fontFamily: "'DM Mono', monospace" }}>ASSIGN WARD (overrides island)</label>
              {(() => {
                const wardPool = editIsland
                  ? allVoters.filter(v => v.island === editIsland)
                  : allVoters;
                const wards = [...new Set(wardPool.map(v => v.ward).filter(Boolean))].sort();
                return (
                  <select value={editWard} onChange={e => { setEditWard(e.target.value); if (e.target.value) setEditIsland(''); }}
                    style={{ width: '100%', padding: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-strong)', fontSize: 13, fontFamily: "'DM Mono', monospace", marginBottom: 18 }}>
                    <option value=''>— None —</option>
                    {wards.map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                );
              })()}
            </>
          )}

          {/* Household bulk assignment — enabled once an island is chosen (territory or search island) */}
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 5, fontFamily: "'DM Mono', monospace" }}>
            ADD HOUSEHOLD {!editHouseholdIsland && <span style={{ color: 'var(--text-4)' }}>(select island first)</span>}
          </label>
          {(() => {
            // Build household groups from voters in the search island (persists even after territory is cleared)
            const pool = editHouseholdIsland ? allVoters.filter(v => v.island === editHouseholdIsland) : [];
            const hhMap = {};
            for (const v of pool) {
              const key = v.household_id || v.house_name || v.house || null;
              if (!key) continue;
              if (!hhMap[key]) hhMap[key] = { key, label: v.house_name || v.house || key, island: v.island, ward: v.ward, members: [] };
              hhMap[key].members.push(v);
            }
            const allHH = Object.values(hhMap).sort((a, b) => a.label.localeCompare(b.label));
            const q = editHouseholdSearch.toLowerCase();
            const filtered = q ? allHH.filter(hh => hh.label.toLowerCase().includes(q) || hh.ward?.toLowerCase().includes(q)) : allHH;

            return (
              <div style={{ position: 'relative', marginBottom: 18 }}>
                <input
                  value={editHouseholdSearch}
                  onChange={e => setEditHouseholdSearch(e.target.value)}
                  onFocus={() => setEditHouseholdOpen(true)}
                  onBlur={() => setTimeout(() => setEditHouseholdOpen(false), 150)}
                  placeholder={editHouseholdIsland ? `Search ${allHH.length} households in ${editHouseholdIsland}…` : 'Select island first'}
                  disabled={!editHouseholdIsland}
                  style={{ width: '100%', padding: '9px 11px', background: editHouseholdIsland ? 'var(--surface-2)' : '#0a1525', border: '1px solid var(--border)', borderRadius: 6, color: editHouseholdIsland ? 'var(--text-strong)' : 'var(--text-4)', fontSize: 13, fontFamily: "'DM Mono', monospace", boxSizing: 'border-box', cursor: editHouseholdIsland ? 'text' : 'not-allowed' }}
                />
                {editHouseholdOpen && editHouseholdIsland && filtered.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 20, maxHeight: 220, overflowY: 'auto' }}>
                    {filtered.slice(0, 20).map(hh => (
                      <div key={hh.key}
                        onMouseDown={() => {
                          hh.members.forEach(v => addVoterAssignment(v));
                          setEditHouseholdSearch('');
                          setEditIsland('');
                          setEditWard('');
                        }}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12, fontFamily: "'DM Mono', monospace" }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ color: 'var(--text-strong)' }}>{hh.label}</span>
                        {hh.ward && <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>{hh.ward}</span>}
                        <span style={{ color: 'var(--text-4)', marginLeft: 8 }}>({hh.members.length})</span>
                      </div>
                    ))}
                    {filtered.length > 20 && (
                      <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace" }}>
                        {filtered.length - 20} more — type to filter
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Individual voter assignment */}
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 5, fontFamily: "'DM Mono', monospace" }}>ASSIGN SPECIFIC VOTERS</label>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input
              value={editVoterSearch}
              onChange={e => setEditVoterSearch(e.target.value)}
              placeholder="Search by name, island or ward..."
              style={{ width: '100%', padding: '9px 11px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-strong)', fontSize: 13, fontFamily: "'DM Mono', monospace", boxSizing: 'border-box' }}
            />
            {editVoterSearch.trim().length >= 2 && (() => {
              const q = editVoterSearch.toLowerCase();
              const results = allVoters
                .filter(v => !editAssignedVoters.find(a => a.id === v.id))
                .filter(v => v.name?.toLowerCase().includes(q) || v.island?.toLowerCase().includes(q) || v.ward?.toLowerCase().includes(q))
                .slice(0, 8);
              return results.length > 0 ? (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 10, maxHeight: 200, overflowY: 'auto' }}>
                  {results.map(v => (
                    <div key={v.id} onClick={() => addVoterAssignment(v)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12, fontFamily: "'DM Mono', monospace" }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ color: 'var(--text-strong)' }}>{v.name}</span>
                      <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>{v.island}{v.ward ? ` · ${v.ward}` : ''}</span>
                    </div>
                  ))}
                </div>
              ) : null;
            })()}
          </div>

          {/* Assigned voters — grouped by household */}
          {loadingAssigned ? (
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginBottom: 16, fontFamily: "'DM Mono', monospace" }}>Loading assignments...</div>
          ) : editAssignedVoters.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {(() => {
                // Group by household key
                const hhMap = {};
                const solo = [];
                for (const v of editAssignedVoters) {
                  const hhKey = v.household_id || v.house_name || v.house || null;
                  if (hhKey) {
                    if (!hhMap[hhKey]) hhMap[hhKey] = { key: hhKey, label: v.house_name || v.house || hhKey, island: v.island, members: [] };
                    hhMap[hhKey].members.push(v);
                  } else {
                    solo.push(v);
                  }
                }
                const households = Object.values(hhMap).map(hh => {
                  const total = allVoters.filter(v => (v.household_id || v.house_name || v.house || null) === hh.key).length;
                  return { ...hh, total };
                });
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {households.map(hh => {
                      const isOpen = expandedHouseholds.has(hh.label);
                      const missing = hh.total - hh.members.length;
                      const incomplete = missing > 0;
                      return (
                        <div key={hh.label} style={{ background: 'var(--surface)', border: `1px solid ${incomplete ? '#78350f' : 'var(--border)'}`, borderRadius: 8, overflow: 'hidden' }}>
                          <div
                            onClick={() => toggleHousehold(hh.label)}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', userSelect: 'none' }}
                          >
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--link)', fontFamily: "'DM Mono', monospace", display: 'flex', alignItems: 'center', gap: 6 }}>
                              🏠 {hh.label}{hh.island ? ` · ${hh.island}` : ''}
                              <span style={{ color: incomplete ? '#f59e0b' : 'var(--text-4)', fontWeight: 400 }}>({hh.members.length}/{hh.total})</span>
                              {incomplete && (
                                <span style={{ fontSize: 10, background: '#78350f', color: '#fbbf24', borderRadius: 4, padding: '1px 6px' }}>
                                  {missing} missing
                                </span>
                              )}
                              <span style={{ color: 'var(--text-4)', fontWeight: 400 }}>{isOpen ? '▲' : '▼'}</span>
                            </span>
                            <button
                              onClick={e => { e.stopPropagation(); hh.members.forEach(v => removeVoterAssignment(v.id)); }}
                              style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 11, fontFamily: "'DM Mono', monospace" }}
                              onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}>
                              Remove
                            </button>
                          </div>
                          {isOpen && (() => {
                            const assignedIds = new Set(hh.members.map(v => v.id));
                            const missingVoters = allVoters.filter(v =>
                              (v.household_id || v.house_name || v.house || null) === hh.key && !assignedIds.has(v.id)
                            );
                            return (
                              <div style={{ padding: '6px 12px 10px' }}>
                                {/* Assigned members */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: missingVoters.length ? 8 : 0 }}>
                                  {hh.members.map(v => (
                                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 20, padding: '2px 8px 2px 7px', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                                      <span style={{ color: 'var(--text)' }}>{v.name}</span>
                                      <button onClick={() => removeVoterAssignment(v.id)}
                                        style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 0 0 3px' }}
                                        onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}>×</button>
                                    </div>
                                  ))}
                                </div>
                                {/* Missing voters */}
                                {missingVoters.length > 0 && (
                                  <div style={{ borderTop: '1px dashed #78350f', paddingTop: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                      <span style={{ fontSize: 10, color: '#f59e0b', fontFamily: "'DM Mono', monospace" }}>MISSING</span>
                                      <button
                                        onClick={() => missingVoters.forEach(v => addVoterAssignment(v))}
                                        style={{ fontSize: 10, background: '#78350f', color: '#fbbf24', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#92400e'}
                                        onMouseLeave={e => e.currentTarget.style.background = '#78350f'}>
                                        + Add all
                                      </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                      {missingVoters.map(v => (
                                        <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#1c0f00', border: '1px dashed #92400e', borderRadius: 20, padding: '2px 8px 2px 7px', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                                          <span style={{ color: '#fbbf24' }}>{v.name}</span>
                                          <button onClick={() => addVoterAssignment(v)}
                                            style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 0 0 3px' }}
                                            onMouseEnter={e => e.currentTarget.style.color = '#fbbf24'}
                                            onMouseLeave={e => e.currentTarget.style.color = '#92400e'}>+</button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {solo.map(v => (
                      <div key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px 3px 8px', fontSize: 11, fontFamily: "'DM Mono', monospace", alignSelf: 'flex-start' }}>
                        <span style={{ color: 'var(--link)' }}>{v.name}</span>
                        <span style={{ color: 'var(--text-4)', fontSize: 10 }}>{v.island ? ` · ${v.island}` : ''}</span>
                        <button onClick={() => removeVoterAssignment(v.id)}
                          style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 0 0 4px' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}>×</button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--surface-3)', marginBottom: 16, fontFamily: "'DM Mono', monospace" }}>No individual voters assigned</div>
          )}

          {/* Temporary permissions — only super_admin / campaign_manager can grant */}
          {(user?.role === 'super_admin' || user?.role === 'campaign_manager') && (
            <div style={{ marginBottom: 18 }}>
              <div onClick={() => setShowPerms(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showPerms ? 6 : 0, cursor: 'pointer', userSelect: 'none' }}>
                <span style={{ fontSize: 11, color: '#f59e0b', fontFamily: "'DM Mono', monospace", letterSpacing: '0.05em' }}>
                  ⚡ PERMISSIONS {showPerms ? '▲' : '▼'}
                  {(editTempPerms.length > 0 || editRevokedPerms.length > 0) && (
                    <span style={{ marginLeft: 6, color: '#ef4444', fontSize: 10 }}>● modified</span>
                  )}
                </span>
                {showPerms && (editTempPerms.length > 0 || editRevokedPerms.length > 0) && (
                  <button onClick={e => { e.stopPropagation(); setEditTempPerms([]); setEditRevokedPerms([]); }}
                    style={{ fontSize: 10, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono', monospace", padding: 0 }}>
                    reset all
                  </button>
                )}
              </div>
              {showPerms && <div style={{ background: 'var(--surface)', border: '1px solid #2d3f1f', borderRadius: 6, overflow: 'hidden' }}>
                {TEMP_PERMISSION_OPTIONS.map((opt, i) => {
                  const tempGranted = editTempPerms.includes(opt.key);
                  const revoked     = editRevokedPerms.includes(opt.key);
                  const fromRole    = hasPermission(editRole, opt.key);
                  const isOn        = fromRole ? !revoked : tempGranted;
                  const bgColor     = revoked ? '#200a0a' : tempGranted ? '#0f2200' : 'transparent';
                  return (
                    <div
                      key={opt.key}
                      onClick={() => {
                        if (fromRole) {
                          // Toggle revocation of a role permission
                          setEditRevokedPerms(prev => revoked ? prev.filter(p => p !== opt.key) : [...prev, opt.key]);
                        } else {
                          // Toggle temp grant of a non-role permission
                          setEditTempPerms(prev => tempGranted ? prev.filter(p => p !== opt.key) : [...prev, opt.key]);
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 12px',
                        borderTop: i > 0 ? '1px solid #1a2a1a' : 'none',
                        cursor: 'pointer',
                        background: bgColor,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = revoked ? '#2d0f0f' : tempGranted ? '#162e00' : '#111f0d'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = bgColor; }}
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={isOn}
                        style={{ accentColor: revoked ? '#ef4444' : '#f59e0b', flexShrink: 0, width: 14, height: 14 }}
                      />
                      <span style={{
                        fontSize: 12,
                        color: revoked ? '#ef4444' : tempGranted ? '#fbbf24' : 'var(--text-2)',
                        fontFamily: "'DM Mono', monospace",
                        flex: 1,
                        textDecoration: revoked ? 'line-through' : 'none',
                      }}>
                        {opt.label}
                      </span>
                      {fromRole && !revoked && (
                        <span style={{ fontSize: 10, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>from role</span>
                      )}
                      {fromRole && revoked && (
                        <span style={{ fontSize: 10, color: '#ef4444', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>revoked</span>
                      )}
                      {!fromRole && tempGranted && (
                        <span style={{ fontSize: 10, color: '#f59e0b', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>granted</span>
                      )}
                    </div>
                  );
                })}
              </div>}
            </div>
          )}

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 14, fontFamily: "'DM Mono', monospace" }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => { setEditingMember(null); setError(''); }}
              style={{ padding: '10px 20px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', cursor: 'pointer', fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
              Cancel
            </button>
            <button onClick={saveRole} disabled={saving}
              style={{ padding: '10px 20px', background: saving ? 'var(--surface-3)' : '#15803d', border: 'none', borderRadius: 6, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
